/**
 * Portuguese render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'pt')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const portugueseLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'desfoque' },
    change: { primary: 'mudança' },
    click: { primary: 'clique' },
    dblclick: { primary: 'duploClique' },
    focus: { primary: 'foco' },
    input: { primary: 'entrada' },
    keydown: { primary: 'tecla baixo' },
    keypress: { primary: 'teclaPressionar' },
    keyup: { primary: 'tecla cima' },
    load: { primary: 'carregar' },
    mousedown: { primary: 'mouse pressionado' },
    mouseenter: { primary: 'mouseEntrar' },
    mouseleave: { primary: 'mouseSair' },
    mousemove: { primary: 'mouseMover' },
    mouseout: { primary: 'mouse fora' },
    mouseover: { primary: 'mouse sobre' },
    mouseup: { primary: 'mouse solto' },
    reset: { primary: 'redefinir' },
    resize: { primary: 'redimensionar' },
    scroll: { primary: 'rolar' },
    submit: { primary: 'envio' },
    touchcancel: { primary: 'toqueCancelar' },
    touchend: { primary: 'toqueFim' },
    touchmove: { primary: 'toqueMover' },
    touchstart: { primary: 'toqueInício' },
    unload: { primary: 'descarregar' },
  },
  logical: {
    and: { primary: 'e' },
    changes: { primary: 'muda' },
    contains: { primary: 'contém' },
    else: { primary: 'senão' },
    end: { primary: 'fim' },
    equals: { primary: 'igual' },
    exists: { primary: 'existe' },
    has: { primary: 'tem' },
    have: { primary: 'tenho' },
    includes: { primary: 'inclui' },
    is: { primary: 'é' },
    live: { primary: 'vivo' },
    matches: { primary: 'corresponde' },
    not: { primary: 'não' },
    or: { primary: 'ou' },
    otherwise: { primary: 'caso_contrário' },
    then: { primary: 'então' },
    when: { primary: 'quando' },
    where: { primary: 'onde' },
  },
  temporal: {
    forever: { primary: 'sempre' },
    h: { primary: 'h' },
    hour: { primary: 'hora' },
    hours: { primary: 'horas' },
    millisecond: { primary: 'milissegundo' },
    milliseconds: { primary: 'milissegundos' },
    min: { primary: 'min' },
    minute: { primary: 'minuto' },
    minutes: { primary: 'minutos' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'segundo' },
    seconds: { primary: 'segundos' },
    times: { primary: 'vezes' },
  },
  values: {
    body: { primary: 'corpo' },
    detail: { primary: 'detalhe' },
    document: { primary: 'documento' },
    element: { primary: 'elemento' },
    event: { primary: 'evento' },
    false: { primary: 'falso' },
    it: { primary: 'isso' },
    its: { primary: 'seu' },
    me: { primary: 'eu' },
    my: { primary: 'meu' },
    myself: { primary: 'eu mesmo' },
    null: { primary: 'nulo' },
    result: { primary: 'resultado' },
    target: { primary: 'alvo' },
    true: { primary: 'verdadeiro' },
    undefined: { primary: 'indefinido' },
    value: { primary: 'valor' },
    window: { primary: 'janela' },
    you: { primary: 'você' },
    your: { primary: 'seu' },
    yourself: { primary: 'você mesmo' },
  },
  attributes: {
    attribute: { primary: 'atributo' },
    attributes: { primary: 'atributos' },
    class: { primary: 'classe' },
    classes: { primary: 'classes' },
    properties: { primary: 'propriedades' },
    property: { primary: 'propriedade' },
    style: { primary: 'estilo' },
    styles: { primary: 'estilos' },
  },
  expressions: {
    up: { primary: 'acima' },
    down: { primary: 'abaixo' },
    left: { primary: 'esquerda' },
    right: { primary: 'direita' },
    at: { primary: 'em' },
    back: { primary: 'atrás' },
    characters: { primary: 'caracteres' },
    children: { primary: 'filhos' },
    closest: { primary: 'maispróximo' },
    elsewhere: { primary: 'exterior' },
    empty: { primary: 'vazio' },
    'ends with': { primary: 'termina com' },
    exclusive: { primary: 'exclusivo' },
    first: { primary: 'primeiro' },
    'ignoring case': { primary: 'ignorando maiúsculas' },
    inclusive: { primary: 'inclusivo' },
    index: { primary: 'índice' },
    'joined by': { primary: 'unido por' },
    last: { primary: 'último' },
    'mapped to': { primary: 'mapeado para' },
    next: { primary: 'próximo' },
    no: { primary: 'nenhum' },
    parent: { primary: 'pai' },
    prev: { primary: 'ant' },
    previous: { primary: 'anterior' },
    random: { primary: 'aleatório' },
    some: { primary: 'algum' },
    'sorted by': { primary: 'ordenado por' },
    'split by': { primary: 'dividido por' },
    start: { primary: 'começar' },
    catch: { primary: 'capturar' },
    finally: { primary: 'finalmente' },
    def: { primary: 'função' },
    'starts with': { primary: 'começa com' },
    within: { primary: 'dentro' },
  },
};

registerLexicon('pt', portugueseLexicon);
